import { LeadField } from '@/pages/Novi/pages/Dashboard/Campagins/AICampaign/types';
import { User, UserAccount } from '@/types/TAccounts';
import apiClient from '@/utils/apiClient';

const FETCH_RESELLER_CAMPAIGNS = '/fetch_reseller_outbound_clients';
const FETCH_CAMPAIGNS = '/api/novi/campaigns';
const FETCH_RECEPTIONIST_TOKEN = '/api/novi/campaigns/:campaign_id/token';
const GET_CAMPAIGN_BY_ID = '/api/novi/campaigns/:campaign_id';
const UPDATE_CAMPAIGN_NAME = '/api/novi/campaigns/name';
const DUPLICATE_CAMPAIGN = '/api/novi/campaigns/duplicate';
const DELETE_CAMPAIGN = '/api/novi/campaigns/delete';
const DEDUCT_CAMPAIGN = '/api/novi/deduct-campaign-subscription-quantity';
const CAMPAIGN_LOGIN = '/api/novi/campaign/login';
const GET_CAMPAIGN_DETAILS = '/api/novi/campaign/details';
const GET_RESERVED_NUMBER = '/api/novi/campaign/:campaign_id/reserved-number';
const CAMPAIGN_ONBOARDING = '/api/novi/campaign/onboarding';
const AI_CAMPAIGN_CREATION = '/api/novi/register-campaign-ai';
const NOVI_CAMPAIGN_STATUS = '/api/novi/campaign/';

const NOVI = 'NOVI';
const NOVI_RECEPTIONIST = 'NOVI_RECEPTIONIST';

export const getNoviCampaigns = async (
	clientType: 'Reseller' | 'Client',
): Promise<{ items: any[] }> => {
	try {
		const response = await apiClient.request({
			method: clientType === 'Reseller' ? 'POST' : 'GET',
			clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
			endPoint: clientType === 'Reseller' ? FETCH_RESELLER_CAMPAIGNS : FETCH_CAMPAIGNS,
			body: {
				shouldFetchReservedNumbers: true,
			},
		});

		return response;
	} catch (error) {
		console.error('Error fetching campaigns:', error);
		throw error;
	}
};

export const getCampaignById = async (
	campaign_id: string,
): Promise<{ success: boolean; campaign?: any }> => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			clientType: NOVI,
			endPoint: GET_CAMPAIGN_BY_ID.replace(':campaign_id', campaign_id),
		});

		return response;
	} catch (error) {
		console.error('Error fetching campaigns:', error);
		throw error;
	}
};

export const getCampaignToken = async (
	clientType: 'Reseller' | 'Client',
	campaign_id: string,
): Promise<{ success: boolean; auth_token: string }> => {
	try {
		const response = await apiClient.request({
			method: 'GET',
			clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
			endPoint: FETCH_RECEPTIONIST_TOKEN.replace(':campaign_id', campaign_id),
		});

		return response;
	} catch (error) {
		console.error('Error fetching campaigns:', error);
		throw error;
	}
};

export const updateCampaignName = async (payload: {
	campaign_id: string;
	name: string;
	campaignGoal: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'PATCH',
		clientType: payload.clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
		endPoint: UPDATE_CAMPAIGN_NAME,
		body: {
			campaign_id: payload.campaign_id,
			name: payload.name,
			campaignGoal: payload.campaignGoal,
		},
	});

	return response;
};

export const duplicateACampaign = async (payload: {
	campaign_id: string;
	name?: string;
	username?: string;
	password?: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{ success: boolean }> => {
	const response = await apiClient.request({
		method: 'POST',
		clientType: payload.clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
		endPoint: DUPLICATE_CAMPAIGN,
		body: payload,
	});

	return response;
};

export const deleteCampaign = async ({
	campaign_id,
	clientType,
}: {
	campaign_id: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{ success: boolean; message: string }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: DELETE_CAMPAIGN,
		body: {
			campaign_id,
		},
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const deductCampaignFromSubscription = async (
	clientType: 'Reseller' | 'Client',
): Promise<{ success: boolean; message: string }> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: DEDUCT_CAMPAIGN,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
	});

	return response;
};

export const loginCampaign = async (payload: {
	username: string;
	password: string;
}): Promise<any> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CAMPAIGN_LOGIN,
		body: payload,
	});

	return response;
};

export const getCampaignDetails = async (): Promise<{
	success: boolean;
	data?: { user: UserAccount; agent: User };
	message?: string;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_CAMPAIGN_DETAILS,
		clientType: 'RECEPTIONIST',
	});

	return response;
};

export const getCampaignReservedNumber = async (
	campaignId: string,
): Promise<{
	success: boolean;
	message?: string;
	data?: {
		reservedNumber: string;
		status: boolean;
	};
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: GET_RESERVED_NUMBER.replace(':campaign_id', campaignId),
		// clientType: NOVI_RECEPTIONIST,
	});

	return response;
};

export const getCampaignOnboarding = async ({
	agentId,
}: {
	agentId: string;
}): Promise<{
	success: boolean;
	message?: string;
	steps?: Array<number>;
}> => {
	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${CAMPAIGN_ONBOARDING}/${agentId}`,
		clientType: NOVI,
	});

	return response;
};

export const updateCampaignOnboarding = async (payload: {
	step?: number;
	steps?: Array<number>;
	agentId: string;
}): Promise<{
	success: boolean;
	message?: string;
	steps?: Array<number>;
}> => {
	const { agentId, ...body } = payload;
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${CAMPAIGN_ONBOARDING}/${agentId}`,
		clientType: NOVI,
		body,
	});

	return response;
};

export const updateCampaignStatus = async (payload: {
	clientId: string;
	status: boolean;
}): Promise<{
	success: boolean;
	message?: string;
	client?: any;
}> => {
	const response = await apiClient.request({
		method: 'PUT',
		endPoint: `${NOVI_CAMPAIGN_STATUS}${payload.clientId}/status`,
		clientType: NOVI_RECEPTIONIST,
		body: { status: payload.status },
	});

	return response;
};

export const createCampaignAI = async (payload: {
	name: string;
	businessName: string;
	email: string;
	username: string;
	campaignGoal: string;
	communicationType: string;
	leadSource: string;
	fieldToCollect: Array<LeadField>;
	userId: string;
	clientType: 'Reseller' | 'Client';
}): Promise<{
	success: boolean;
	message?: string;
	data?: any;
}> => {
	const { clientType, ...body } = payload;
	const response = await apiClient.request({
		method: 'POST',
		endPoint: AI_CAMPAIGN_CREATION,
		clientType: clientType === 'Reseller' ? 'RESELLER' : 'RECEPTIONIST',
		body,
	});

	return response;
};
